import React from "react";
// Consumer Management Modal

// Default
import { useNavigate } from "react-router-dom";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";

// Custom
import CustomDataTable from "../table/CustomTable";
import ContainedButton from "../../forms/button/ContainedButton";

// Utils
import { firstLetterUppercase, formatDate } from "../../utils/helper";

// React-icons
import { MdEdit } from "react-icons/md";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const ConsumerManagementModal = ({
  detailModal,
  setDetailModal,
  selectedNode,
  setOpenModal,
}: {
  detailModal: any;
  setDetailModal: (detailModal: any) => void;
  selectedNode: any;
  setOpenModal: (openModal: any) => void;
}) => {
  const navigate = useNavigate();

  const handleClose = () => {
    setDetailModal({
      type: "",
      open: false,
    });
  };

  const getFullName = (item: any) => {
    const { firstName, lastName, name, title } = item;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (name) {
      return name;
    } else if (title) {
      return title;
    } else {
      return "-";
    }
  };
  const fetchColumns = () => {
    switch (detailModal?.type) {
      case "CONSUMER":
        return ["Id", "Name", "Type", "Actions"];
      case "CONTRACT":
        return ["Id", "Category", "Type", "Start Date", "End Date", "Actions"];
      case "LOCATION":
        return ["Id", "Type", "Address", "Actions"];
      case "SITE":
        return [
          "Id",
          "Operating Hours",
          "Commodity Type",
          "Service Type",
          "Actions",
        ];
      case "METER":
        return ["Id", "Manufacturer", "Type", "Installation Date", "Actions"];
      default:
        return [];
    }
  };

  const fetchRows = () => {
    switch (detailModal?.type) {
      case "CONSUMER":
        return selectedNode?.map((item: any, index: number) => (
          <TableRow
            key={index}
            sx={{
              height: "50px",
              background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
            }}
            className="custom_table_tbody"
          >
            <TableCell>
              <Typography className="custom_table_tbody_cell">
                {item?.id || "-"}
              </Typography>
            </TableCell>

            <TableCell>
              <Typography className="custom_table_tbody_cell">
                {getFullName(item)}
              </Typography>
            </TableCell>

            <TableCell>
              <Typography className="custom_table_tbody_cell">
                {item?.consumerType || "-"}
              </Typography>
            </TableCell>

            <TableCell>
              <UnifiedIcon
                icon={MdEdit}
                color="#999B9D"
                fontSize="16px"
                onClick={() => {
                  setOpenModal({
                    type: detailModal?.type?.toUpperCase(),
                    id: item?.id,
                  });
                  handleClose();
                }}
              />
            </TableCell>
          </TableRow>
        ));
      case "CONTRACT":
        return selectedNode?.map((item: any, index: number) => {
          const { commodityCategory, commodityType, startdate, enddate } =
            item?.additionalInfo;

          return (
            <TableRow
              key={index}
              sx={{
                height: "50px",
                background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
              }}
              className="custom_table_tbody"
            >
              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {item?.id?.id || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {commodityCategory || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {commodityType || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {startdate
                    ? formatDate(new Date(startdate), "DD/MM/YYYY HH:mm:ss")
                    : "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {enddate
                    ? formatDate(new Date(enddate), "DD/MM/YYYY HH:mm:ss")
                    : "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <UnifiedIcon
                  icon={MdEdit}
                  color="#999B9D"
                  fontSize="16px"
                  onClick={() => {
                    setOpenModal({
                      type: detailModal?.type?.toUpperCase(),
                      id: item?.id?.id,
                    });
                    handleClose();
                  }}
                />
              </TableCell>
            </TableRow>
          );
        });
      case "LOCATION":
        return selectedNode?.map((item: any, index: number) => {
          const { consumerType, city, houseNo, state, streetAddress, zip } =
            item?.additionalInfo;

          return (
            <TableRow
              key={index}
              sx={{
                height: "50px",
                background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
              }}
              className="custom_table_tbody"
            >
              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {item?.id?.id || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {consumerType || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {houseNo} {streetAddress}, {city}, {state}, {zip}{" "}
                </Typography>
              </TableCell>

              <TableCell>
                <UnifiedIcon
                  icon={MdEdit}
                  color="#999B9D"
                  fontSize="16px"
                  onClick={() => {
                    setOpenModal({
                      type: detailModal?.type?.toUpperCase(),
                      id: item?.id?.id,
                    });
                    handleClose();
                  }}
                />
              </TableCell>
            </TableRow>
          );
        });
      case "SITE":
        return selectedNode?.map((item: any, index: number) => {
          const { operatingHours, serviceCommodityType, serviceType } =
            item?.additionalInfo;

          return (
            <TableRow
              key={index}
              sx={{
                height: "50px",
                background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
              }}
              className="custom_table_tbody"
            >
              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {item?.id?.id || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {operatingHours || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {serviceCommodityType || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {serviceType || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <UnifiedIcon
                  icon={MdEdit}
                  color="#999B9D"
                  fontSize="16px"
                  onClick={() => {
                    setOpenModal({
                      type: detailModal?.type?.toUpperCase(),
                      id: item?.id?.id,
                    });
                    handleClose();
                  }}
                />
              </TableCell>
            </TableRow>
          );
        });
      case "METER":
        return selectedNode?.map((item: any, index: number) => {
          const { InstallationDate, manufacturer, meterCommodityType } =
            item?.additionalInfo;

          return (
            <TableRow
              key={index}
              sx={{
                height: "50px",
                background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
              }}
              className="custom_table_tbody"
            >
              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {item?.id?.id || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {manufacturer || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {meterCommodityType || "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography className="custom_table_tbody_cell">
                  {InstallationDate
                    ? formatDate(
                        new Date(InstallationDate),
                        "dd/MM/yyyy HH:mm:ss"
                      )
                    : "-"}
                </Typography>
              </TableCell>

              <TableCell>
                <UnifiedIcon
                  icon={MdEdit}
                  color="#999B9D"
                  fontSize="16px"
                  onClick={() => {
                    navigate("/meters", { state: { meterid: item?.id?.id } });
                    handleClose();
                  }}
                />
              </TableCell>
            </TableRow>
          );
        });
      default:
        return [];
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      open={detailModal?.open}
      fullWidth
      maxWidth={"md"}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {firstLetterUppercase(detailModal?.type)}
        </DialogTitle>

        <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
          {firstLetterUppercase(detailModal?.type) !== "Consumer" && (
            <ContainedButton
              label={`Add ${firstLetterUppercase(detailModal?.type)}`}
              sx={{
                width: "fit-content",
                color: "#ffffff",
              }}
              onClick={() => {
                if (detailModal?.type === "METER") {
                  navigate("/meters");
                } else {
                  setOpenModal({
                    type: detailModal?.type?.toUpperCase(),
                    id: "",
                  });
                }
                handleClose();
              }}
            />
          )}
          <IconButton onClick={handleClose} sx={{ marginRight: "10px" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>

      <DialogContent dividers>
        <CustomDataTable
          sx={{ maxHeight: "600px" }}
          columns={fetchColumns()}
          pagination={false}
          rowsContent={fetchRows()}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ConsumerManagementModal;
